import { useState } from "react";
import { Loader2, AlertCircle, Plus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { apiFetch, ApiError } from "@/lib/api-client";
import {
  AccountTree,
  AccountSearch,
  AccountFormDrawer,
  useAccounts,
  useAccountTree,
  type AccountTreeNode,
  type AccountFormValues,
} from "@/features/accounts";

/**
 * Chart of Accounts page.
 *
 * Displays accounts in a hierarchical tree view with:
 * - Search filtering (preserves path/ancestors)
 * - Expand/collapse controls
 * - Create new accounts (root or sub-account)
 * - Edit via side drawer
 */
export function CuentasPage() {
  const { accounts, isLoading, error, refetch } = useAccounts();
  const [searchQuery, setSearchQuery] = useState("");
  const [editingAccount, setEditingAccount] = useState<AccountTreeNode | null>(
    null,
  );
  const [parentAccount, setParentAccount] = useState<AccountTreeNode | null>(
    null,
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const {
    tree,
    isExpanded,
    toggleExpanded,
    expandAll,
    collapseAll,
    matchCount,
  } = useAccountTree({
    accounts,
    searchQuery,
  });

  // Open drawer in edit mode
  const handleEdit = (account: AccountTreeNode) => {
    setEditingAccount(account);
    setParentAccount(null);
    setIsDrawerOpen(true);
  };

  // Open drawer to create a new root account
  const handleCreateNew = () => {
    setEditingAccount(null);
    setParentAccount(null);
    setIsDrawerOpen(true);
  };

  // Open drawer to create a sub-account under the given parent
  const handleCreateChild = (parent: AccountTreeNode) => {
    setEditingAccount(null);
    setParentAccount(parent);
    setIsDrawerOpen(true);
  };

  // Save handler - creates or updates based on mode
  const handleSave = async (values: AccountFormValues) => {
    try {
      if (editingAccount) {
        // UPDATE existing account
        await apiFetch.put(`/api/accounts/${editingAccount.id}`, {
          code: values.code,
          name: values.name,
          accountType: values.accountType,
          accountSubtype: values.accountSubtype,
          normalBalance: values.normalBalance,
          isActive: values.isActive,
          isHeader: values.isHeader,
          description: values.description,
        });
        toast.success("Cuenta actualizada correctamente");
      } else {
        // CREATE new account
        await apiFetch.post("/api/accounts", {
          code: values.code,
          name: values.name,
          accountType: values.accountType,
          accountSubtype: values.accountSubtype,
          normalBalance: values.normalBalance,
          parentAccountId: values.parentAccountId,
          isHeader: values.isHeader,
          description: values.description,
        });
        toast.success("Cuenta creada correctamente");
      }

      // Close drawer and refresh list
      setIsDrawerOpen(false);
      setEditingAccount(null);
      setParentAccount(null);
      refetch();
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message);
      } else {
        toast.error("Error al guardar la cuenta");
      }
    }
  };

  const handleDrawerOpenChange = (open: boolean) => {
    setIsDrawerOpen(open);
    if (!open) {
      setEditingAccount(null);
      setParentAccount(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-2 text-destructive">
        <AlertCircle className="h-8 w-8" />
        <p>Error al cargar las cuentas</p>
        <p className="text-sm text-muted-foreground">{error.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Plan de Cuentas</h1>
          <p className="text-sm text-muted-foreground">
            {accounts.length} cuentas en total
          </p>
        </div>
      </div>

      {/* Search and controls */}
      <div className="flex items-center justify-between gap-4">
        <AccountSearch
          value={searchQuery}
          onChange={setSearchQuery}
          matchCount={matchCount}
          totalCount={accounts.length}
          onExpandAll={expandAll}
          onCollapseAll={collapseAll}
        />
        <Button onClick={handleCreateNew}>
          <Plus className="mr-2 h-4 w-4" />
          Agregar Cuenta
        </Button>
      </div>

      {/* Account tree */}
      <div className="border rounded-lg p-4 bg-card">
        <AccountTree
          tree={tree}
          isExpanded={isExpanded}
          toggleExpanded={toggleExpanded}
          onEdit={handleEdit}
          onCreateChild={handleCreateChild}
        />
      </div>

      {/* Create/Edit drawer */}
      <AccountFormDrawer
        account={editingAccount}
        parentAccount={parentAccount}
        accounts={accounts}
        open={isDrawerOpen}
        onOpenChange={handleDrawerOpenChange}
        onSave={handleSave}
      />
    </div>
  );
}
